/**
 * Dashboard Live Visitors — GA-style realtime block for the home dashboard.
 *
 * Sits under the storefront banner: active visitors now, per-minute chart
 * (last 30 min), and top pages / traffic sources snapshots. Links to the
 * full Realtime page (/analytics/realtime). Hidden entirely while loading
 * or when analytics isn't configured, so it never adds noise for tenants
 * without PostHog data.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, LineChart, Line } from 'recharts'
import { ChevronRight, Monitor, Smartphone, TrendingDown, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useActiveNow, useDailyStats, useRealtimeOverview, type SparkPair } from '@/hooks/useLiveVisitors'
import { LiveActivityFeedCompact, sourceName, sourceLogoUrl, VisitorStack } from './LiveActivityFeed'

function MiniBreakdown({
  title,
  rows,
}: {
  title: string
  rows: { label: string; count: number; image?: string; avatars?: string[]; anonCount?: number }[]
}) {
  if (rows.length === 0) return null
  const max = Math.max(...rows.map((r) => r.count), 1)
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
        {title}
      </p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-1.5 min-w-0">
                {r.image && (
                  <img
                    src={r.image}
                    alt=""
                    loading="lazy"
                    className="h-4 w-4 rounded object-cover shrink-0 border border-border/40"
                  />
                )}
                <span className="truncate text-foreground" title={r.label}>
                  {r.label}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {r.avatars && (
                  <VisitorStack avatars={r.avatars} anonCount={r.anonCount ?? 0} size="h-5 w-5" />
                )}
                <span className="tabular-nums text-muted-foreground">{r.count}</span>
              </span>
            </div>
            {!r.avatars && (
              <div className="mt-0.5 h-1 rounded-full bg-muted/60 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary/60 transition-all duration-500"
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const NUM = new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 })

/** Rolls a number toward its new value when real data arrives — the
 * "alive" ticker feel without ever fabricating counts. */
function useTicking(target: number, duration = 900): number {
  const [display, setDisplay] = useState(target)
  const prevRef = useRef(target)
  useEffect(() => {
    const from = prevRef.current
    if (from === target) return
    prevRef.current = target
    const start = performance.now()
    let raf: number
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      setDisplay(from + (target - from) * eased)
      if (p < 1) raf = requestAnimationFrame(step)
      else setDisplay(target)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])
  return display
}

function TickingValue({ value, fmt }: { value: number; fmt: (n: number) => string }) {
  const shown = useTicking(value)
  return <>{fmt(shown)}</>
}

function TileSpark({ spark }: { spark: SparkPair }) {
  const points = spark.cur.map((c, i) => ({ i, cur: c, prev: spark.prev[i] ?? 0 }))
  if (points.every((p) => p.cur === 0 && p.prev === 0)) return null
  return (
    <div className="mt-2 h-8 w-full max-w-[150px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <Line
            dataKey="prev"
            stroke="hsl(var(--muted-foreground) / 0.35)"
            strokeWidth={1.25}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="cur"
            stroke="hsl(var(--primary))"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function DailyStatTile({
  label,
  raw,
  fmt,
  sub,
  delta,
  spark,
  invertDelta,
  to,
}: {
  label: string
  raw: number
  fmt: (n: number) => string
  sub?: string
  delta?: number | null
  spark?: SparkPair
  invertDelta?: boolean
  to?: string
}) {
  const Wrapper: React.ElementType = to ? Link : 'div'
  return (
    <Wrapper
      {...(to ? { to } : {})}
      className={cn(
        'relative block min-w-0 px-4 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-px before:bg-border/60',
        to && 'cursor-pointer rounded-lg hover:bg-muted/30 transition-colors',
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <p className="text-xl font-semibold text-foreground tabular-nums">
          <TickingValue value={raw} fmt={fmt} />
        </p>
        {delta != null && delta !== 0 && (
          <span
            className={cn(
              'flex shrink-0 items-center gap-0.5 text-[11px] font-medium tabular-nums',
              (invertDelta ? delta < 0 : delta > 0)
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-500 dark:text-red-400',
            )}
            title="เทียบกับ 24 ชม. ก่อนหน้า"
          >
            {delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {delta > 0 ? '+' : ''}{delta}%
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{label}{sub ? ` · ${sub}` : ''}</p>
      {spark && <TileSpark spark={spark} />}
    </Wrapper>
  )
}

export function DashboardLiveVisitors() {
  const { data, isLoading } = useRealtimeOverview()
  const { data: now } = useActiveNow()
  const { data: daily } = useDailyStats()

  const chartData = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({
        ...s,
        time: s.minute.replace('T', ' ').slice(11, 16),
      })),
    [data?.series],
  )

  // Reserve the block's space immediately — popping in late shifts the layout
  if (isLoading) {
    return (
      <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
          <div className="h-4 w-40 bg-muted rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-6 items-end">
          <div className="space-y-2">
            <div className="h-10 w-16 bg-muted rounded animate-pulse" />
            <div className="h-3 w-24 bg-muted rounded animate-pulse" />
          </div>
          <div className="h-20 sm:h-24 bg-muted/60 rounded animate-pulse" />
        </div>
      </section>
    )
  }

  // Quietly absent for tenants without analytics
  if (!data || !data.configured) return null

  // Fast lane wins when available — it refreshes every 10s vs 30s
  const visitors = now?.active_visitors ?? data.active_visitors
  const devices = data.devices

  const pageRows = data.top_pages.slice(0, 3).map((p) => {
    const slash = p.page.indexOf('/')
    return {
      label: p.product_name || (slash >= 0 ? p.page.slice(slash) : p.page),
      image: p.product_image || undefined,
      count: p.visitors,
      avatars: p.avatars ?? [],
      anonCount: p.anon_count ?? 0,
    }
  })
  const referrerRows = data.referrers.slice(0, 3).map((r) => {
    const label = r.source === 'direct' ? 'เข้าตรง (Direct)' : sourceName(r.source)
    return {
      label: label + (r.via ? ` · ผ่าน ${r.via}` : ''),
      image: sourceLogoUrl(label) ?? undefined,
      count: r.visitors,
      avatars: r.avatars ?? [],
      anonCount: r.anon_count ?? 0,
    }
  })

  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span
              className={cn(
                'absolute inline-flex h-full w-full rounded-full opacity-60',
                visitors > 0 && 'animate-ping bg-green-500',
              )}
            />
            <span
              className={cn(
                'relative inline-flex h-2 w-2 rounded-full',
                visitors > 0 ? 'bg-green-500' : 'bg-muted-foreground/40',
              )}
            />
          </span>
          <h3 className="text-sm font-semibold text-foreground">ผู้ชมเว็บแบบเรียลไทม์</h3>
        </div>
        <Link
          to="/analytics/realtime"
          className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          ดูทั้งหมด
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Number + chart */}
      <div className="grid grid-cols-[auto_1fr] gap-4 sm:gap-6 items-end">
        <div>
          <p className="text-4xl font-semibold text-foreground tabular-nums leading-none">
            <TickingValue value={visitors} fmt={(n) => String(Math.round(n))} />
          </p>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            กำลังชมตอนนี้ · 5 นาทีล่าสุด
          </p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
            <span className="flex items-center gap-1">
              <Smartphone className="h-3 w-3" /> {devices.mobile}
            </span>
            <span className="flex items-center gap-1">
              <Monitor className="h-3 w-3" /> {devices.desktop + devices.tablet}
            </span>
          </div>
        </div>

        <div className="h-20 sm:h-24 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="time"
                tick={{ fontSize: 9 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={48}
              />
              <Tooltip
                cursor={{ fill: 'hsl(var(--primary) / 0.08)' }}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(value, name) => [
                  value ?? 0,
                  name === 'visitors' ? 'ผู้เข้าชม' : 'เพจวิว',
                ]}
              />
              <Bar dataKey="visitors" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 24h stats strip */}
      {daily?.configured && (
        <div className="mt-5 pt-4 border-t border-border/30">
          <div className="flex items-baseline justify-between gap-2 mb-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              24 ชั่วโมงล่าสุด
            </p>
            <p className="text-[10px] text-muted-foreground/70">
              % เทียบกับ 24 ชม. ก่อนหน้า
            </p>
          </div>
          <div className="overflow-hidden">
            {/* 2-up on phones, then auto-fit to the CONTAINER width (not the
                viewport) — this strip sits in a narrow col-span-7/8 column, so
                viewport breakpoints (lg:grid-cols-6) crammed all 6 tiles into
                one row on laptops. auto-fit keeps each tile >=150px wide. */}
            <div className="grid grid-cols-2 sm:[grid-template-columns:repeat(auto-fit,minmax(150px,1fr))] gap-y-6 -ml-px">
            <DailyStatTile label="ผู้เข้าชม" raw={daily.visitors} fmt={(n) => NUM.format(Math.round(n))} delta={daily.deltas?.visitors} spark={daily.spark?.visitors} />
            <DailyStatTile label="เพจวิว" raw={daily.pageviews} fmt={(n) => NUM.format(Math.round(n))} delta={daily.deltas?.pageviews} spark={daily.spark?.pageviews} />
            <DailyStatTile label="คอนเวอร์ชัน" raw={daily.conversion_pct} fmt={(n) => `${n.toFixed(1)}%`} delta={daily.deltas?.conversion} spark={daily.spark?.conversion} />
            <DailyStatTile
              label="ในตะกร้า"
              raw={daily.cart_value}
              fmt={(n) => `฿${NUM.format(Math.round(n))}`}
              sub={`${NUM.format(daily.cart_count)} ใบ`}
              delta={daily.deltas?.cart_value}
              spark={daily.spark?.cart_value}
              to="/analytics/carts"
            />
            <DailyStatTile
              label="ยอดขาย"
              raw={daily.sales_value}
              fmt={(n) => `฿${NUM.format(Math.round(n))}`}
              sub={`${NUM.format(daily.sales_count)} ออเดอร์`}
              delta={daily.deltas?.sales_value}
              spark={daily.spark?.sales_value}
            />
            <DailyStatTile
              label="ทิ้งตะกร้า"
              raw={daily.abandon_pct ?? 0}
              fmt={(n) => `${n.toFixed(1)}%`}
              sub="ตะกร้าที่ยังไม่สั่งซื้อ"
              delta={daily.deltas?.abandon}
              spark={daily.spark?.abandon}
              invertDelta
            />

            </div>
          </div>
        </div>
      )}

      {/* Latest shopper activity */}
      <div className="mt-5 pt-2 border-t border-border/30">
        <LiveActivityFeedCompact limit={5} />
        <Link
          to="/analytics/realtime"
          className="mt-1 flex items-center justify-center gap-0.5 rounded-lg py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
        >
          ดูเหตุการณ์ทั้งหมด
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Top pages + sources snapshot */}
      {(pageRows.length > 0 || referrerRows.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8 mt-5 pt-4 border-t border-border/30">
          <MiniBreakdown title="หน้าที่มีคนดู" rows={pageRows} />
          <MiniBreakdown title="แหล่งที่มา" rows={referrerRows} />
        </div>
      )}
    </section>
  )
}
